'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; /*  weak */


exports.default = createRenderer;

var _generateStyleHash = require('./utils/generateStyleHash');

var _generateStyleHash2 = _interopRequireDefault(_generateStyleHash);

var _getFontFormat = require('./utils/getFontFormat');

var _getFontFormat2 = _interopRequireDefault(_getFontFormat);

var _processStyle = require('./utils/processStyle');

var _processStyle2 = _interopRequireDefault(_processStyle);

var _diffStyle = require('./utils/diffStyle');

var _diffStyle2 = _interopRequireDefault(_diffStyle);

var _cssifyKeyframe = require('./utils/cssifyKeyframe');

var _cssifyKeyframe2 = _interopRequireDefault(_cssifyKeyframe);

var _cssifyObject = require('./utils/cssifyObject');

var _cssifyObject2 = _interopRequireDefault(_cssifyObject);

var _warning = require('./utils/warning');

var _warning2 = _interopRequireDefault(_warning);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * creates a new renderer instance
 *
 * @param {Object} config - renderer configuration
 * @return {Object} new renderer instance
 */
function createRenderer() {
  var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  // the renderer is the key
  var renderer = {
    listeners: [],
    keyframePrefixes: config.keyframePrefixes || ['-webkit-', '-moz-'],
    plugins: config.plugins || [],

    // try and use readable selectors when
    // prettySelectors is on and not in a prod environment
    prettySelectors: config.prettySelectors && process.env.NODE_ENV !== 'production',
    mediaQueryOrder: config.mediaQueryOrder || [],

    /**
     * clears the sheet's cache but keeps all listeners
     */
    clear: function clear() {
      renderer.fontFaces = '';
      renderer.keyframes = '';
      renderer.statics = '';
      renderer.rules = '';
      renderer.mediaRules = renderer.mediaQueryOrder.reduce(function (rules, media) {
        rules[media] = '';
        return rules;
      }, {});

      renderer.rendered = {};
      renderer.base = [];
      renderer.ids = [];
      renderer.baseClassName = [];
      renderer.callStack = [];

      // emit changes to notify subscribers
      renderer._emitChange({ type: 'clear' });
    },


    /**
     * renders a new rule variation and caches the result
     *
     * @param {Function} rule - rule which gets rendered
     * @param {Object?} props - properties used to render
     * @param {Object?} defaultProps - properties used to render the static style
     * @return {string} className to reference the rendered rule
     */
    renderRule: function renderRule(rule) {
      var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      var defaultProps = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

      // rendering a rule for the first time
      // will create an ID reference
      if (renderer.ids.indexOf(rule) === -1) {
        renderer.ids.push(rule);

        // directly render the static base style to be able
        // to diff future dynamic style with those
        try {
          renderer.renderRule(rule, defaultProps, defaultProps);
        } catch (error) {
          process.env.NODE_ENV !== "production" ? (0, _warning2.default)(false, 'Nested props have been used without passing \'defaultProps\'. This will disable static style splitting for \'' + (rule.name ? rule.name : 'unkown_rule') + '\'.') : void 0;
        }
      }

      var ruleId = renderer.ids.indexOf(rule);

      var ruleProps = _extends({}, defaultProps, props);

      var style = rule(ruleProps);
      var styleId = renderer._generateStyleId(style);

      var className = 'c' + styleId;

      // extend the className with prefixes in development
      // this enables better debugging and className readability
      if (process.env.NODE_ENV !== 'production') {
        className = (renderer._selectorPrefix ? renderer._selectorPrefix + '__' : '') + (renderer.prettySelectors && rule.name ? rule.name + '__' : '') + className;
      }

      // only if the rule has not already been rendered
      // with a specific set of properties it actually renders
      if (!renderer.rendered.hasOwnProperty(className)) {
        // process style using each plugin
        var processedStyle = (0, _processStyle2.default)(style, {
          type: 'rule',
          className: className,
          props: ruleProps,
          rule: rule
        }, renderer.plugins);

        // diff style objects with base styles
        var diffedStyle = (0, _diffStyle2.default)(processedStyle, renderer.base[ruleId]);
        renderer.rendered[className] = false;

        if (Object.keys(diffedStyle).length > 0) {
          renderer._renderStyle(className, diffedStyle);
        }

        renderer.callStack.push(renderer.renderRule.bind(renderer, rule, props, defaultProps));

        // keep static style to diff dynamic onces later on
        if (props === defaultProps) {
          renderer.base[ruleId] = diffedStyle;
          renderer.baseClassName[ruleId] = className;
          return renderer.rendered[className] ? className : '';
        }
      }

      var baseClassName = renderer.baseClassName[ruleId];

      // if current className is empty
      // return either the static class or empty string
      if (!renderer.rendered[className]) {
        return renderer.rendered[baseClassName] ? baseClassName : '';
      }

      // if the current className is a dynamic rule
      // return both classNames if static subset is not empty
      if (className !== baseClassName) {
        return (renderer.rendered[baseClassName] ? baseClassName + ' ' : '') + className;
      }

      return className;
    },


    /**
     * renders a new keyframe variation and caches the result
     *
     * @param {Keyframe} keyframe - Keyframe which gets rendered
     * @param {Object?} props - properties used to render
     * @return {string} animationName to reference the rendered keyframe
     */
    renderKeyframe: function renderKeyframe(keyframe) {
      var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var style = keyframe(props);
      var styleId = renderer._generateStyleId(style);

      var animationName = 'k' + styleId;

      // extend the animationName with prefixes in development
      // this enables better debugging and className readability
      if (process.env.NODE_ENV !== 'production') {
        animationName = (renderer.prettySelectors && keyframe.name ? keyframe.name + '__' : '') + animationName;
      }

      // only if the keyframe has not already been rendered
      // with a specific set of properties it actually renders
      if (!renderer.rendered.hasOwnProperty(animationName)) {
        var processedKeyframe = (0, _processStyle2.default)(style, {
          type: 'keyframe',
          keyframe: keyframe,
          props: props,
          animationName: animationName
        }, renderer.plugins);

        var css = (0, _cssifyKeyframe2.default)(processedKeyframe, animationName, renderer.keyframePrefixes);
        renderer.rendered[animationName] = true;
        renderer.keyframes += css;

        renderer.callStack.push(renderer.renderKeyframe.bind(renderer, keyframe, props));
        renderer._emitChange({
          name: animationName,
          style: processedKeyframe,
          css: css,
          type: 'keyframe'
        });
      }

      return animationName;
    },


    /**
     * renders a new font-face and caches it
     *
     * @param {FontFace} fontFace - fontFace which gets rendered
     * @return {string} fontFamily reference
     */
    renderFont: function renderFont(family, files) {
      var properties = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

      var key = family + (0, _generateStyleHash2.default)(properties);

      if (!renderer.rendered.hasOwnProperty(key)) {
        (function () {
          var fontFace = {
            fontFamily: '\'' + family + '\'',
            src: files.map(function (src) {
              return 'url(\'' + src + '\') format(\'' + (0, _getFontFormat2.default)(src) + '\')';
            }).join(',')
          };

          var fontProperties = ['fontVariant', 'fontWeight', 'fontStretch', 'fontStyle', 'unicodeRange'];
          Object.keys(properties).filter(function (prop) {
            return fontProperties.indexOf(prop) > -1;
          }).forEach(function (fontProp) {
            return fontFace[fontProp] = properties[fontProp];
          });

          var css = '@font-face{' + (0, _cssifyObject2.default)(fontFace) + '}';

          renderer.rendered[key] = true;
          renderer.fontFaces += css;

          renderer.callStack.push(renderer.renderFont.bind(renderer, family, files, properties));

          renderer._emitChange({
            fontFamily: family,
            fontFace: fontFace,
            css: css,
            type: 'font'
          });
        })();
      }

      return family;
    },


    /**
     * renders static style and caches them
     *
     * @param {string|Object} style - static style to be rendered
     * @param {string?} selector - selector used to render the styles
     * @return {string} rendered CSS output
     */
    renderStatic: function renderStatic(style, selector) {
      var reference = typeof style === 'string' ? style : selector + JSON.stringify(style);

      if (!renderer.rendered.hasOwnProperty(reference)) {
        if (typeof style === 'string') {
          var css = style.replace(/\s{2,}/g, '');
          // remove new lines from template strings
          renderer.statics += css;
          renderer._emitChange({
            selector: selector,
            type: 'static',
            css: css
          });
        } else {
          var processedStyle = (0, _processStyle2.default)(style, {
            selector: selector,
            type: 'static'
          }, renderer.plugins);

          var _css = (0, _cssifyObject2.default)(processedStyle);
          renderer.statics += selector + '{' + _css + '}';

          renderer.callStack.push(renderer.renderStatic.bind(renderer, style, selector));

          renderer._emitChange({
            selector: selector,
            style: processedStyle,
            css: _css,
            type: 'rule'
          });
        }

        renderer.rendered[reference] = true;
      }
    },


    /**
     * renders all cached styles into a single valid CSS string
     * clusters media query styles into groups to reduce output size
      * @return single concatenated CSS string
     */
    renderToString: function renderToString() {
      var css = renderer.fontFaces + renderer.statics + renderer.rules;

      for (var media in renderer.mediaRules) {
        var rules = renderer.mediaRules[media];
        if (rules.length > 0) {
          css += '@media ' + media + '{' + rules + '}';
        }
      }

      return css + renderer.keyframes;
    },


    /**
     * Adds a new subscription to get notified on every rerender
     *
     * @param {Function} callback - callback function which will be executed
     * @return {Object} equivalent unsubscribe method
     */
    subscribe: function subscribe(callback) {
      renderer.listeners.push(callback);
      return {
        unsubscribe: function unsubscribe() {
          return renderer.listeners.splice(renderer.listeners.indexOf(callback), 1);
        }
      };
    },


    /**
     * rehydrates the whole cache using the callStack
     */
    rehydrate: function rehydrate() {
      var callStack = renderer.callStack.slice(0);

      // clears the current callStack
      renderer.clear();

      renderer._emitChange({ type: 'rehydrate', done: false });
      callStack.forEach(function (fn) {
        return fn();
      });
      renderer._emitChange({ type: 'rehydrate', done: true });
    },


    /**
     * generates a unique style id
     *
     * @param {Object} style - style object
     * @return {string} minimal string id
     */
    _generateStyleId: function _generateStyleId(style) {
      var styleHash = (0, _generateStyleHash2.default)(style);

      if (renderer.ids.indexOf(styleHash) === -1) {
        renderer.ids.push(styleHash);
      }

      return renderer.ids.indexOf(styleHash).toString(36);
    },


    /**
     * calls each listener with a change object
     * gets only called if something actually changes
     *
     * @param {Function} callback - callback function which will be executed
     * @return {Object} equivalent unsubscribe method
     */
    _emitChange: function _emitChange(change) {
      renderer.listeners.forEach(function (listener) {
        return listener(change, renderer);
      });
    },


    /**
     * iterates a style object and renders each rule to the cache
     *
     * @param {string} className - className reference to be rendered to
     * @param {Object} style - style object which is rendered
     */
    _renderStyle: function _renderStyle(className, style) {
      var pseudo = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
      var media = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';

      var ruleset = Object.keys(style).reduce(function (ruleset, property) {
        var value = style[property];
        // recursive object iteration in order to render
        // pseudo class and media class declarations
        if (value instanceof Object && !Array.isArray(value)) {
          // allow pseudo classes, attribute selectors and the child selector
          if (property.match(/^(:|\[|>)/) !== null) {
            renderer._renderStyle(className, value, pseudo + property, media);
          } else if (property.substr(0, 6) === '@media') {
            // combine media query rules with an `and`
            var query = property.slice(6).trim();
            var combinedMedia = media.length > 0 ? media + ' and ' + query : query;
            renderer._renderStyle(className, value, pseudo, combinedMedia);
          }
        } else {
          ruleset[property] = value;
        }
        return ruleset;
      }, {});

      // add styles to the cache
      if (Object.keys(ruleset).length > 0) {
        renderer.rendered[className] = true;

        var css = (0, _cssifyObject2.default)(ruleset);
        var selector = '.' + className + pseudo;
        var cssRule = selector + '{' + css + '}';

        if (media.length > 0) {
          if (!renderer.mediaRules.hasOwnProperty(media)) {
            renderer.mediaRules[media] = '';
          }

          renderer.mediaRules[media] += cssRule;
        } else {
          renderer.rules += cssRule;
        }

        renderer._emitChange({
          selector: selector,
          style: ruleset,
          css: css,
          media: media,
          type: 'rule'
        });
      }
    }
  };

  // initial setup
  renderer.keyframePrefixes.push('');
  renderer.clear();

  // enhance renderer with passed set of enhancers
  if (config.enhancers) {
    config.enhancers.forEach(function (enhancer) {
      return renderer = enhancer(renderer);
    });
  }

  return renderer;
}
module.exports = exports['default'];


//////////////////
// WEBPACK FOOTER
// ../~/fela/lib/createRenderer.js
// module id = 435
// module chunks = 0